"use client";

import Breadcrumb from "@/components/Common/Breadcrumb";
import { getImagePath } from "@/lib/utils";
import { trackCtaClicked } from "@/lib/analytics";
import React from "react";
import Testimonials from "@/components/Testimonials";
import Benefits from "@/components/Benefits";
import HowItWork from "@/components/HowItWorks";
import { useTranslations } from "next-intl";
import { useLocale } from "next-intl";
import { formatNumberByLocale } from "@/utils/numberFormatter";
import { PARTNER_GUIDE_DOWNLOAD_ENABLED } from "@/components/constants/general";

interface BenefitItem {
  id: number;
  heading: string;
  points: string[];
}

const Page = () => {
  const t = useTranslations("benefitsForPartners");
  const locale = useLocale();

  // Use t.raw() to get the raw array object
  ///adding a comment to test
  const benefitsItems = (t.raw("items") || []) as BenefitItem[];
  // Only en/mr/hi have the Own & Earn copy; other languages keep their old text.
  const buyLabel = t.has("buttons.buyStick") ? t("buttons.buyStick") : t("buttons.joinNow");

  return (
    <section className="section-padding bg-white py-24">
      <Breadcrumb
        pageName={String(t("title"))}
        breadcrumbName={t.has("breadcrumbName") ? String(t("breadcrumbName")) : undefined}
        description={t.has("subtitle") ? String(t("subtitle")) : ""}
        bgImages={[getImagePath("/images/breadcrumb/joinPartner.webp")]}
        bgIndex={0}
        buttons={[
          {
            label: buyLabel,
            href: "/buynow-customer-campaign",
            props: {
              variant: "default",
              className:
                "ml-2 rounded-tl-xl rounded-br-xl bg-white text-primary hover:bg-primary hover:text-white",
            },
          },
          {
            label: t("buttons.calculator"),
            href: "/own-and-earn/calculator",
            props: {
              variant: "default",
              className:
                "ml-2 rounded-tl-xl rounded-br-xl bg-white text-primary hover:bg-primary hover:text-white",
            },
          },
        ]}
      />

      {t.has("subtitle") && (
        <div className="container mx-auto md:px-6 mt-5">
          <p className="text-xl md:text-2xl font-semibold text-[#A23465]">{t("subtitle")}</p>
        </div>
      )}

      {/* Full Information Banner */}
      {PARTNER_GUIDE_DOWNLOAD_ENABLED && (
      <div className="container mx-auto md:px-6 mt-5">
        <div className="py-4 md:py-5 flex flex-col md:flex-row items-start justify-between gap-6">
          <div className="leading-relaxed">
            <h2 className="text-primary text-3xl">
              {t("fullInfo")}
            </h2>
            <p>
              {t("fullInfoDesc")}
            </p>
          </div>
          <button
            type="button"
            onClick={() => {
              trackCtaClicked({ cta: "download_pdf", page: "benefits_partner" });
              const url = t("fullInfoLink");
              window.open(url, "_blank");
              const a = document.createElement("a");
              a.href = url;
              a.download = "";
              document.body.appendChild(a);
              a.click();
              document.body.removeChild(a);
            }}
            className="inline-flex items-center gap-3 bg-orange-500 hover:bg-orange-600 text-white font-bold text-sm md:text-lg uppercase tracking-wider px-8 md:px-10 py-4 md:py-5 rounded-tl-2xl rounded-br-2xl shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300 whitespace-nowrap"
          >
            {t("downloadPdf")}
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 16l-5-5h3V4h4v7h3l-5 5zm-7 2h14v2H5v-2z" />
            </svg>
          </button>
        </div>
      </div>
      )}

      <div className="container grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-20 items-center justify-center mt-5">
        <div>
          {benefitsItems.slice(0, 4).map((item) => (
            <div key={item.id} className="flex items-start mb-10 gap-5">
              <h1 className="numbering md:text-8xl text-[#A23465]">
                {formatNumberByLocale(locale, item.id)}
              </h1>

              <div>
                <h2 className="text-2xl md:text-3xl font-semibold text-[#A23465] mb-4">
                  {item.heading}
                </h2>
                <ul className="list-item BenefitsPart list-inside text-lg text-body-color space-y-2">
                  {item.points.map((p, i) => (
                    <li key={i}>{p}</li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>
        <div>
          <img
            src={getImagePath("/images/benefits/BF-right.webp")}
            alt={t("title")}
            className="w-full h-auto mx-auto"
          />
        </div>
      </div>

      <div className="container grid grid-cols-1 lg:grid-cols-2 gap-5 lg:gap-20 items-start justify-center mt-5">
        {benefitsItems.slice(4).map((item) => (
          <div key={item.id} className="flex items-start gap-2">
            <h1 className="numbering md:text-8xl text-[#A23465]">
              {formatNumberByLocale(locale, item.id)}
            </h1>

            <div>
              <h2 className="text-2xl md:text-3xl font-semibold text-[#A23465] mb-4">
                {item.heading}
              </h2>
              <ul className="list-item BenefitsPart list-inside text-lg text-body-color space-y-2">
                {item.points.map((p, i) => (
                  <li key={i}>{p}</li>
                ))}
              </ul>
            </div>
          </div>
        ))}
      </div>

      <div className="container mt-10 mb-24">
        <p className="text-[#575757]">{t("description")}</p>
        <div className="text-center mt-10 pb-8 flex flex-col items-center relative">
          <a
            href="/buynow-customer-campaign"
            className="bg-gradient-to-r z-10 from-[#A23465] to-[#EA7F75] text-white rounded-tl-xl rounded-br-xl inline-flex items-center justify-center px-4 py-2 text-sm font-medium"
          >
            {buyLabel}
          </a>
        </div>

        <div className="flex justify-center items-center mt-10 pb-8 relative w-full">
          <div className="w-full max-w-6xl">
            <Benefits benefitsToShow={[2, 3]} />
          </div>
        </div>
      </div>

      <HowItWork />
      {/* <Testimonials /> */}
    </section>
  );
};

export default Page;
